import { NavLink, useLocation } from 'react-router-dom';

/**
 * Sticky bottom navigation bar for mobile (md:hidden).
 *
 * Renders 4–5 shortcut icons fixed to the bottom of the viewport — same UX as
 * native mobile apps. Each item supports an optional `match` predicate so that
 * non-exact paths can still light up the icon (e.g. /dashboard/products/123 lights
 * the "Products" icon).
 *
 * Props:
 *   items: [{ to, icon: LucideIcon, label, match?: (pathname) => boolean, end?: boolean }]
 *
 * Usage: drop near the bottom of a layout, after the <main>. The layout's main
 * content already has its own scroll container — this floats above it.
 */
export default function MobileBottomNav({ items = [], primaryColor = 'var(--store-primary, #2D6CDF)' }) {
  const loc = useLocation();

  return (
    <>
      {/* Spacer so the last bit of main content isn't hidden behind the floating bar.
          We render this only on mobile so desktop layout is untouched. */}
      <div className="md:hidden h-16" aria-hidden />

      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-2px_10px_rgba(0,0,0,0.04)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
        aria-label="Mobile bottom navigation"
      >
        <div className="grid grid-cols-5 h-16">
          {items.slice(0, 5).map(({ to, icon: Icon, label, match, end }) => {
            const isActive = match ? match(loc.pathname) : (end ? loc.pathname === to : loc.pathname.startsWith(to));
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={`flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${
                  isActive ? '' : 'text-slate-500 hover:text-slate-900'
                }`}
                style={isActive ? { color: primaryColor } : undefined}
              >
                <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                <span className="truncate max-w-[60px]">{label}</span>
                {isActive && <span className="absolute top-0 h-0.5 w-8 rounded-b" style={{ backgroundColor: primaryColor }} />}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </>
  );
}
